import React, { useState, useEffect, useCallback } from 'react';
import { Users, BarChart2, Plus, Search, Edit2, Key, Power, X, Check, AlertCircle, ChevronDown } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';

// ── STAT CARD ──
function StatCard({ label, value, color = '#0066FF' }) {
  return (
    <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 10, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ fontFamily: 'var(--font-heading)', fontSize: 32, fontWeight: 800, color, letterSpacing: -1, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    </div>
  );
}

// ── USER MODAL ──
function UserModal({ user, onClose, onSave }) {
  const isEdit = !!user?.id;
  const [form, setForm] = useState({
    fullName: user?.full_name || '',
    email: user?.email || '',
    companyName: user?.company_name || '',
    password: '',
    planType: user?.plan_type || 'monthly',
    subscriptionStatus: user?.subscription_status || 'active',
    isActive: user?.is_active !== false,
    adminNotes: user?.admin_notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    if (!isEdit && form.password.length < 8) e.password = 'Min 8 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/users/${user.id}`, {
          fullName: form.fullName,
          companyName: form.companyName,
          planType: form.planType,
          subscriptionStatus: form.subscriptionStatus,
          isActive: form.isActive,
          adminNotes: form.adminNotes,
        });
        toast.success('User updated');
      } else {
        await api.post('/admin/users', { ...form, facilityLimit: form.facilityLimit ? parseInt(form.facilityLimit) : null });
        toast.success('User created');
      }
      onSave();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-body)', color: '#0A1628', outline: 'none', background: 'white' };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 };
  const fieldStyle = { marginBottom: 14 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 14, width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: '#0A1628' }}>{isEdit ? 'Edit Customer' : 'New Customer'}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4 }}><X size={18} /></button>
        </div>
        <div style={{ padding: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Full Name *</label>
              <input style={{ ...inputStyle, borderColor: errors.fullName ? '#FECACA' : '#E2E8F0' }}
                value={form.fullName} onChange={e => setForm({ ...form, fullName: e.target.value })} placeholder="Jane Smith" />
              {errors.fullName && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 3 }}>{errors.fullName}</div>}
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Company</label>
              <input style={inputStyle} value={form.companyName} onChange={e => setForm({ ...form, companyName: e.target.value })} placeholder="Acme Manufacturing" />
            </div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Email *</label>
            <input style={{ ...inputStyle, borderColor: errors.email ? '#FECACA' : '#E2E8F0', background: isEdit ? '#F8FAFC' : 'white' }}
              value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
              disabled={isEdit} placeholder="jane@company.com" />
          </div>

          {!isEdit && (
            <div style={fieldStyle}>
              <label style={labelStyle}>Password *</label>
              <input type="password" style={{ ...inputStyle, borderColor: errors.password ? '#FECACA' : '#E2E8F0' }}
                value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Min 8 characters" />
              {errors.password && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 3 }}>{errors.password}</div>}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Plan</label>
              <select style={inputStyle} value={form.planType} onChange={e => setForm({ ...form, planType: e.target.value })}>
                <option value="starter">Starter (1 facility)</option>
                <option value="growth">Growth (5 facilities)</option>
                <option value="business">Business (25 facilities)</option>
                <option value="enterprise">Enterprise (50 facilities)</option>
                <option value="partner">Partner / Reseller</option>
                <option value="trial">Trial</option>
              </select>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Status</label>
              <select style={inputStyle} value={form.subscriptionStatus} onChange={e => setForm({ ...form, subscriptionStatus: e.target.value })}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="past_due">Past Due</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {isEdit && (
            <div style={{ ...fieldStyle, display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ ...labelStyle, margin: 0 }}>Account Active</label>
              <button onClick={() => setForm({ ...form, isActive: !form.isActive })}
                style={{ width: 40, height: 22, borderRadius: 11, background: form.isActive ? '#0066FF' : '#E2E8F0', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                <div style={{ width: 16, height: 16, borderRadius: 8, background: 'white', position: 'absolute', top: 3, left: form.isActive ? 21 : 3, transition: 'left 0.2s' }} />
              </button>
            </div>
          )}

          <div style={fieldStyle}>
            <label style={labelStyle}>Custom Facility Limit <span style={{color:'#94a3b8',fontWeight:400}}>(overrides plan default)</span></label>
            <input type="number" min="1" max="9999"
              style={inputStyle}
              value={form.facilityLimit}
              onChange={e => setForm({ ...form, facilityLimit: e.target.value })}
              placeholder="Leave blank to use plan default" />
            <div style={{fontSize:11,color:'#94a3b8',marginTop:3}}>Starter=1 · Growth=5 · Business=25 · Enterprise=50</div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Internal Notes</label>
            <textarea style={{ ...inputStyle, height: 72, resize: 'vertical' }}
              value={form.adminNotes} onChange={e => setForm({ ...form, adminNotes: e.target.value })}
              placeholder="Internal notes (not visible to customer)" />
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button onClick={onClose} style={{ padding: '9px 18px', border: '1px solid #E2E8F0', borderRadius: 8, background: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '9px 18px', background: saving ? '#94a3b8' : '#0066FF', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-body)' }}>
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Customer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── RESET PASSWORD MODAL ──
function ResetPasswordModal({ user, onClose }) {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleReset = async () => {
    if (password.length < 8) return toast.error('Min 8 characters');
    setSaving(true);
    try {
      await api.post(`/admin/users/${user.id}/reset-password`, { newPassword: password });
      toast.success(`Password reset for ${user.email}`);
      onClose();
    } catch (err) {
      toast.error('Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 14, width: '100%', maxWidth: 400, boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: '#0A1628' }}>Reset Password</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={18} /></button>
        </div>
        <div style={{ padding: 24 }}>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Set a new password for <strong style={{ color: '#0A1628' }}>{user?.email}</strong></div>
          <input type="password" placeholder="New password (min 8 chars)"
            value={password} onChange={e => setPassword(e.target.value)}
            style={{ width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-body)', marginBottom: 16, outline: 'none' }} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ padding: '9px 18px', border: '1px solid #E2E8F0', borderRadius: 8, background: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Cancel</button>
            <button onClick={handleReset} disabled={saving}
              style={{ padding: '9px 18px', background: saving ? '#94a3b8' : '#DC2626', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
              {saving ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MAIN ADMIN PANEL ──
export default function AdminPanel() {
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const usersRes = await api.get('/admin/users');
      setUsers(usersRes.data.users || []);
    } catch (err) {
      console.error('Admin users error:', err);
      toast.error('Failed to load customers');
      setUsers([]);
    }
    try {
      const statsRes = await api.get('/admin/stats');
      setStats(statsRes.data.stats || null);
    } catch (err) {
      console.error('Admin stats error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleToggleActive = async (user) => {
    try {
      await api.post(`/admin/users/${user.id}/toggle-active`);
      toast.success(`Account ${user.is_active ? 'suspended' : 'activated'}`);
      loadData();
    } catch {
      toast.error('Failed to update account');
    }
  };

  const filteredUsers = users.filter(u => {
    const matchSearch = !search ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      u.company_name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && u.is_active && u.subscription_status === 'active') ||
      (filterStatus === 'inactive' && (!u.is_active || u.subscription_status !== 'active'));
    return matchSearch && matchStatus;
  });

  const statusBadge = (user) => {
    if (!user.is_active) return { label: 'Suspended', bg: '#FEF2F2', color: '#DC2626' };
    if (user.subscription_status === 'active') return { label: 'Active', bg: '#F0FDF4', color: '#16A34A' };
    if (user.subscription_status === 'past_due') return { label: 'Past Due', bg: '#FFFBEB', color: '#D97706' };
    return { label: user.subscription_status, bg: '#F8FAFC', color: '#64748b' };
  };

  const planBadge = (plan) => {
    const colors = { starter: { bg: '#EFF6FF', color: '#0066FF' }, growth: { bg: '#F0FDF4', color: '#16A34A' }, business: { bg: '#F5F3FF', color: '#7C3AED' }, enterprise: { bg: '#0A1628', color: 'white' }, partner: { bg: '#ECFEFF', color: '#0891B2' }, trial: { bg: '#FFFBEB', color: '#D97706' } };
    return colors[plan] || { bg: '#EFF6FF', color: '#0066FF' };
  };

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 22, fontWeight: 800, color: '#0A1628', letterSpacing: -0.5 }}>Admin Panel</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>Customer account management</div>
        </div>
        <button onClick={() => setShowCreateModal(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#0066FF', color: 'white', border: 'none', borderRadius: 9, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
          <Plus size={15} /> New Customer
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 10, marginBottom: 24 }}>
          <StatCard label="Total Customers" value={stats.totalUsers} />
          <StatCard label="Active" value={stats.activeUsers} color="#16A34A" />
          <StatCard label="New (30d)" value={stats.newUsersLast30Days} color="#7C3AED" />
          <StatCard label="Documents" value={stats.totalDocuments} color="#D97706" />
          <StatCard label="Tasks" value={stats.totalTasks} color="#0891B2" />
          <StatCard label="Chats" value={stats.totalConversations} color="#DC2626" />
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #E2E8F0', paddingBottom: 0 }}>
        {[{ id: 'users', label: 'Customers', icon: Users }, { id: 'usage', label: 'Usage', icon: BarChart2 }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: tab === t.id ? '#0066FF' : '#64748b', borderBottom: tab === t.id ? '2px solid #0066FF' : '2px solid transparent', marginBottom: -1 }}>
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>

      {/* Search + Filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input placeholder="Search by name, email, or company..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', paddingLeft: 36, paddingRight: 12, paddingTop: 9, paddingBottom: 9, border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none' }} />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none', background: 'white', color: '#374151' }}>
          <option value="all">All Customers</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive / Suspended</option>
        </select>
      </div>

      {/* USERS TAB */}
      {tab === 'users' && (
        <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading customers...</div>
          ) : filteredUsers.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No customers found</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #F1F5F9', background: '#F8FAFC' }}>
                  {['Customer', 'Plan', 'Status', 'Joined', 'Last Login', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, i) => {
                  const status = statusBadge(user);
                  const plan = planBadge(user.plan_type);
                  return (
                    <tr key={user.id} style={{ borderBottom: i < filteredUsers.length - 1 ? '1px solid #F8FAFC' : 'none', opacity: user.is_active ? 1 : 0.6 }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0A1628' }}>{user.full_name || 'No name'}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{user.email}</div>
                        {user.company_name && <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{user.company_name}</div>}
                        {user.admin_notes && <div style={{ fontSize: 11, color: '#D97706', marginTop: 2 }}>📝 {user.admin_notes.slice(0, 40)}{user.admin_notes.length > 40 ? '...' : ''}</div>}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ background: plan.bg, color: plan.color, fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 10 }}>{user.plan_type || 'monthly'}</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ background: status.bg, color: status.color, fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 10 }}>{status.label}</span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748b' }}>
                        {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748b' }}>
                        {user.last_login ? new Date(user.last_login).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Never'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => setEditUser(user)} title="Edit"
                            style={{ padding: '6px 8px', border: '1px solid #E2E8F0', borderRadius: 7, background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#374151' }}>
                            <Edit2 size={13} />
                          </button>
                          <button onClick={() => setResetUser(user)} title="Reset password"
                            style={{ padding: '6px 8px', border: '1px solid #E2E8F0', borderRadius: 7, background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#374151' }}>
                            <Key size={13} />
                          </button>
                          <button onClick={() => handleToggleActive(user)} title={user.is_active ? 'Suspend' : 'Activate'}
                            style={{ padding: '6px 8px', border: `1px solid ${user.is_active ? '#FECACA' : '#BBF7D0'}`, borderRadius: 7, background: user.is_active ? '#FEF2F2' : '#F0FDF4', cursor: 'pointer', display: 'flex', alignItems: 'center', color: user.is_active ? '#DC2626' : '#16A34A' }}>
                            <Power size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* USAGE TAB */}
      {tab === 'usage' && (
        <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading...</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #F1F5F9', background: '#F8FAFC' }}>
                  {['Customer', 'Facilities', 'Documents', 'Tasks', 'AI Chats', 'Status'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, i) => (
                  <tr key={user.id} style={{ borderBottom: i < filteredUsers.length - 1 ? '1px solid #F8FAFC' : 'none' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0A1628' }}>{user.full_name || user.email}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{user.company_name}</div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: '#0066FF' }}>{user.facility_count || 0}</span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: '#7C3AED' }}>{user.document_count || 0}</span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: '#0891B2' }}>{user.task_count || 0}</span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: '#D97706' }}>{user.conversation_count || 0}</span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ background: user.is_active ? '#F0FDF4' : '#FEF2F2', color: user.is_active ? '#16A34A' : '#DC2626', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 10 }}>
                        {user.is_active ? 'Active' : 'Suspended'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modals */}
      {showCreateModal && <UserModal onClose={() => setShowCreateModal(false)} onSave={loadData} />}
      {editUser && <UserModal user={editUser} onClose={() => setEditUser(null)} onSave={loadData} />}
      {resetUser && <ResetPasswordModal user={resetUser} onClose={() => setResetUser(null)} />}
    </div>
  );
}
